import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Input from '../../../../../components/Input';
import Modal from '../../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../components/Notification';
import BasicDatePicker from '../../../../../components/SelectDate';
import SingleSelect from '../../../../../components/SingleSelect';
import HttpStatus from '../../../../../enums/httpStatus';
import { accessBlocksApi } from '../../../../../services/requests';
import { AccessBlock } from '../../../../../types/apiResponse/accessBlock';
import { SelectOption } from '../../../../../utils/getSelectOptions';
import AudienceSelectWithCreate from './AudienceSelectWithCreate';
import {
  accessBlockActionOptions,
  accessBlockScheduleOptions,
  accessBlockTypeOptions,
  timeOptions,
  weekdays,
} from '../constants';
import * as s from '../styles';

type FormData = {
  type: AccessBlock['type'] | null;
  description: string;
  audienceId: number | null;
  audienceOption: SelectOption | null;
  actions: AccessBlock['actions'];
  scheduleType: AccessBlock['scheduleType'] | null;
  weekdays: number[];
  specificDate: string | null;
  startsOn: string | null;
  endsOn: string | null;
  allDay: boolean;
  startTime: string | null;
  endTime: string | null;
};

type Props = {
  showModal: boolean;
  selectedAccessBlock: AccessBlock | null;
  closeModal: () => void;
  callback: () => void;
};

const initialFormData = (): FormData => ({
  type: null,
  description: '',
  audienceId: null,
  audienceOption: null,
  actions: [],
  scheduleType: null,
  weekdays: [],
  specificDate: null,
  startsOn: null,
  endsOn: null,
  allDay: false,
  startTime: null,
  endTime: null,
});

const dateToString = (value: any): string | null => {
  if (!value) {
    return null;
  }

  if (typeof value.format === 'function') {
    return value.format('YYYY-MM-DD');
  }

  return String(value).slice(0, 10);
};

const AccessBlockFormModal = ({
  showModal,
  selectedAccessBlock,
  closeModal,
  callback,
}: Props) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>(initialFormData());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!selectedAccessBlock) {
      setFormData(initialFormData());
      return;
    }

    setFormData({
      type: selectedAccessBlock.type,
      description: selectedAccessBlock.description,
      audienceId: selectedAccessBlock.audienceId,
      audienceOption: selectedAccessBlock.audience
        ? {
          value: selectedAccessBlock.audience.id,
          label: selectedAccessBlock.audience.description,
        }
        : null,
      actions: selectedAccessBlock.actions,
      scheduleType: selectedAccessBlock.scheduleType,
      weekdays: selectedAccessBlock.weekdays,
      specificDate: selectedAccessBlock.specificDate,
      startsOn: selectedAccessBlock.startsOn,
      endsOn: selectedAccessBlock.endsOn,
      allDay: selectedAccessBlock.allDay,
      startTime: selectedAccessBlock.startTime,
      endTime: selectedAccessBlock.endTime,
    });
  }, [selectedAccessBlock]);

  const updateForm = (changes: Partial<FormData>) => {
    setFormData((current) => ({ ...current, ...changes }));
  };

  const notifyInvalid = (message: string) => {
    frontendNotification({ message, type: 'warning' });
  };

  const validateForm = (): boolean => {
    if (!formData.type) {
      notifyInvalid(t('chooseBlockType') as string);
      return false;
    }

    if (!formData.description.trim()) {
      notifyInvalid(t('invalidDescription') as string);
      return false;
    }

    if (!formData.audienceId) {
      notifyInvalid(t('chooseAudience') as string);
      return false;
    }

    if (formData.actions.length === 0) {
      notifyInvalid(t('chooseAtLeastOneAccessAction') as string);
      return false;
    }

    if (!formData.scheduleType) {
      notifyInvalid(t('chooseScheduleType') as string);
      return false;
    }

    if (formData.scheduleType === 'weekday' && formData.weekdays.length === 0) {
      notifyInvalid(t('chooseAtLeastOneWeekday') as string);
      return false;
    }

    if (formData.scheduleType === 'specific_date' && !formData.specificDate) {
      notifyInvalid(t('chooseSpecificDate') as string);
      return false;
    }

    if (formData.scheduleType === 'period' && (!formData.startsOn || !formData.endsOn)) {
      notifyInvalid(t('choosePeriodDates') as string);
      return false;
    }

    if (
      formData.scheduleType === 'period'
      && formData.startsOn
      && formData.endsOn
      && formData.startsOn > formData.endsOn
    ) {
      notifyInvalid(t('startDateMustBeBeforeEndDate') as string);
      return false;
    }

    if (!formData.allDay && (!formData.startTime || !formData.endTime)) {
      notifyInvalid(t('chooseTimeRange') as string);
      return false;
    }

    return true;
  };

  const saveRequest = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    const data = {
      type: formData.type,
      description: formData.description.trim(),
      audienceId: formData.audienceId,
      actions: formData.actions,
      scheduleType: formData.scheduleType,
      weekdays: formData.weekdays,
      specificDate: formData.specificDate,
      startsOn: formData.startsOn,
      endsOn: formData.endsOn,
      allDay: formData.allDay,
      startTime: formData.allDay ? null : formData.startTime,
      endTime: formData.allDay ? null : formData.endTime,
    };

    try {
      accessBlocksApi.query = '';
      const response = selectedAccessBlock
        ? await accessBlocksApi.update(data, selectedAccessBlock.id)
        : await accessBlocksApi.store(data);

      const expectedStatus = selectedAccessBlock ? HttpStatus.OK : HttpStatus.CREATED;
      if (response.status !== expectedStatus) {
        throw response;
      }

      frontendNotification({
        message: t('success') as string,
        type: 'success',
      });
      callback();
      closeModal();
    } catch (err) {
      if (!accessBlocksApi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAction = (action: AccessBlock['actions'][number]) => {
    const actions = formData.actions.includes(action)
      ? formData.actions.filter((item) => item !== action)
      : [...formData.actions, action];

    updateForm({ actions });
  };

  const toggleWeekday = (weekday: number) => {
    const selected = formData.weekdays.includes(weekday)
      ? formData.weekdays.filter((item) => item !== weekday)
      : [...formData.weekdays, weekday];

    updateForm({ weekdays: selected.sort((a, b) => a - b) });
  };

  return (
    <Modal
      title={selectedAccessBlock ? t('editAccessBlock') as string : t('newAccessBlock') as string}
      showModal={showModal}
      closeModal={closeModal}
      cancelButton={closeModal}
      action={saveRequest}
      isLoading={isLoading}
      style={{ maxWidth: '900px', width: 'min(900px, 94vw)' }}
      bodyStyle={{
        maxHeight: '74vh',
        overflowY: 'auto',
        paddingLeft: '1rem',
        paddingRight: '1rem',
        boxSizing: 'border-box',
      }}
    >
      <s.ModalContent>
        <s.Section>
          <s.SectionTitle>{t('accessBlockType')}</s.SectionTitle>
          <s.ChoiceGrid>
            {accessBlockTypeOptions().map((option) => (
              <s.ChoiceButton
                key={option.value}
                type="button"
                $selected={formData.type === option.value}
                $variant={option.value}
                onClick={() => updateForm({ type: option.value })}
                disabled={isLoading}
                aria-pressed={formData.type === option.value}
              >
                <s.ChoiceHeader>
                  <s.ChoiceIndicator
                    $selected={formData.type === option.value}
                    $variant={option.value}
                    aria-hidden="true"
                  >
                    {formData.type === option.value ? '✓' : ''}
                  </s.ChoiceIndicator>
                  <strong>{option.label}</strong>
                </s.ChoiceHeader>
                <s.ChoiceDescription>
                  {option.value === 'block' ? t('accessBlockTypeHint') : t('accessReleaseTypeHint')}
                </s.ChoiceDescription>
              </s.ChoiceButton>
            ))}
          </s.ChoiceGrid>
        </s.Section>

        <s.Section>
          <s.FieldsGrid>
            <s.Field>
              <s.FieldLabel htmlFor="access-block-description">{t('description')}</s.FieldLabel>
              <Input
                id="access-block-description"
                value={formData.description}
                onChange={(event: any) => updateForm({ description: event.target.value })}
                disabled={isLoading}
                isRequired
              />
            </s.Field>
            <s.Field>
              <AudienceSelectWithCreate
                value={formData.audienceOption}
                onChange={(option) => updateForm({
                  audienceOption: option,
                  audienceId: option ? Number(option.value) : null,
                })}
                disabled={isLoading}
              />
            </s.Field>
          </s.FieldsGrid>
        </s.Section>

        <s.Section>
          <s.SectionTitle>{t('accessActions')}</s.SectionTitle>
          <s.CheckboxGrid>
            {accessBlockActionOptions().map((option) => (
              <s.CheckboxCard
                key={option.value}
                $selected={formData.actions.includes(option.value)}
              >
                <input
                  type="checkbox"
                  checked={formData.actions.includes(option.value)}
                  onChange={() => toggleAction(option.value)}
                  disabled={isLoading}
                />
                <s.ActionCopy>
                  <strong>{option.label}</strong>
                  <small>{option.description}</small>
                </s.ActionCopy>
              </s.CheckboxCard>
            ))}
          </s.CheckboxGrid>
          <s.Hint>{t('accessActionsHint')}</s.Hint>
        </s.Section>

        <s.Section>
          <s.SectionTitle>{t('scheduleType')}</s.SectionTitle>
          <s.FieldsGrid>
            <s.Field>
              <SingleSelect
                options={accessBlockScheduleOptions()}
                value={formData.scheduleType
                  ? accessBlockScheduleOptions().find((option) => option.value === formData.scheduleType) ?? null
                  : null}
                onChange={(option: SelectOption | null) => updateForm({
                  scheduleType: option?.value as AccessBlock['scheduleType'] ?? null,
                  weekdays: option?.value === 'weekday' ? formData.weekdays : [],
                  specificDate: option?.value === 'specific_date' ? formData.specificDate : null,
                  startsOn: option?.value === 'period' ? formData.startsOn : null,
                  endsOn: option?.value === 'period' ? formData.endsOn : null,
                })}
                placeholder={t('chooseScheduleType') as string}
                disabled={isLoading}
                isRequired
              />
            </s.Field>
          </s.FieldsGrid>

          {formData.scheduleType === 'weekday' && (
            <s.Field>
              <s.FieldLabel>{t('selectWeekdays')}</s.FieldLabel>
              <s.WeekdayList>
                {weekdays().map((weekday) => (
                  <s.WeekdayButton
                    key={weekday.value}
                    type="button"
                    $selected={formData.weekdays.includes(weekday.value)}
                    onClick={() => toggleWeekday(weekday.value)}
                    disabled={isLoading}
                    aria-pressed={formData.weekdays.includes(weekday.value)}
                  >
                    {weekday.label}
                  </s.WeekdayButton>
                ))}
              </s.WeekdayList>
            </s.Field>
          )}

          {formData.scheduleType === 'specific_date' && (
            <s.Field>
              <s.FieldLabel>{t('specificDate')}</s.FieldLabel>
              <BasicDatePicker
                value={formData.specificDate}
                onChange={(value: any) => updateForm({ specificDate: dateToString(value) })}
                disablePast
                disabled={isLoading}
              />
            </s.Field>
          )}

          {formData.scheduleType === 'period' && (
            <s.FieldsGrid>
              <s.Field>
                <s.FieldLabel>{t('startDate')}</s.FieldLabel>
                <BasicDatePicker
                  value={formData.startsOn}
                  onChange={(value: any) => updateForm({ startsOn: dateToString(value) })}
                  disablePast
                  disabled={isLoading}
                />
              </s.Field>
              <s.Field>
                <s.FieldLabel>{t('endDate')}</s.FieldLabel>
                <BasicDatePicker
                  value={formData.endsOn}
                  onChange={(value: any) => updateForm({ endsOn: dateToString(value) })}
                  disablePast
                  disabled={isLoading}
                />
              </s.Field>
            </s.FieldsGrid>
          )}
        </s.Section>

        <s.Section>
          <s.SectionTitle>{t('timeRange')}</s.SectionTitle>
          <s.InlineCheckbox>
            <input
              type="checkbox"
              checked={formData.allDay}
              onChange={(event) => updateForm({
                allDay: event.target.checked,
                startTime: event.target.checked ? null : formData.startTime,
                endTime: event.target.checked ? null : formData.endTime,
              })}
              disabled={isLoading}
            />
            {t('allDay')}
          </s.InlineCheckbox>

          <s.FieldsGrid>
            <s.Field>
              <s.FieldLabel htmlFor="access-block-start-time">{t('startTime')}</s.FieldLabel>
              <s.NativeSelect
                id="access-block-start-time"
                value={formData.startTime ?? ''}
                onChange={(event) => updateForm({ startTime: event.target.value || null })}
                disabled={isLoading || formData.allDay}
              >
                <option value="">{t('selectTime')}</option>
                {timeOptions().map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </s.NativeSelect>
            </s.Field>
            <s.Field>
              <s.FieldLabel htmlFor="access-block-end-time">{t('endTime')}</s.FieldLabel>
              <s.NativeSelect
                id="access-block-end-time"
                value={formData.endTime ?? ''}
                onChange={(event) => updateForm({ endTime: event.target.value || null })}
                disabled={isLoading || formData.allDay}
              >
                <option value="">{t('selectTime')}</option>
                {timeOptions().map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </s.NativeSelect>
            </s.Field>
          </s.FieldsGrid>
          <s.Hint>{t('overnightTimeHint')}</s.Hint>
        </s.Section>
      </s.ModalContent>
    </Modal>
  );
};

export default AccessBlockFormModal;
